"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const AssertionError_1 = require("../error/AssertionError");
const EtudeError_1 = require("../error/EtudeError");
const CircularIterator_1 = require("../util/CircularIterator");
const ImmutablePrioritySet_1 = require("../util/ImmutablePrioritySet");
const InfiniteIterator_1 = require("../util/InfiniteIterator");
const MathUtil_1 = require("../util/MathUtil");
const StreamUtil_1 = require("../util/StreamUtil");
const Direction_1 = require("./Direction");
const Letter_1 = require("./Letter");
const MusicConstants_1 = require("./MusicConstants");
const Pitch_1 = require("./Pitch");
const Policy_1 = require("./Policy");
class Scale {
    constructor(pitch, parameter, descending) {
        this.pitch = pitch;
        if (parameter instanceof Scale.Quality) {
            this.quality = parameter;
        }
        else {
            const ascending = parameter;
            this.quality = descending === undefined
                ? new Scale.Quality(ascending)
                : new Scale.Quality(ascending, descending);
        }
    }
    getDefaultPolicy(direction) {
        if (this.quality.isOctaveRepeating(direction) && this.quality.getStepPattern(direction).length === Letter_1.default.size) {
            // prioritize unique letters
            const it = Letter_1.default.iterator(direction, this.pitch.getKey().getLetter());
            let current = it.next().value;
            return Policy_1.default.prioritize(k => {
                const pass = k.getLetter() === current;
                // each time the current key finds a match, go to the next letter
                if (pass) {
                    current = it.next().value;
                }
                return pass;
            });
        }
        else {
            return Policy_1.default.DEFAULT_PRIORITY;
        }
    }
    stream(direction = Direction_1.default.DEFAULT, policies) {
        if (direction instanceof ImmutablePrioritySet_1.default) {
            if (arguments.length !== 1) {
                throw EtudeError_1.default.forIllegalArgument(Scale, arguments, "Invalid number of arguments");
            }
            policies = direction;
            direction = Direction_1.default.DEFAULT;
        }
        else if (direction instanceof Direction_1.default) {
            if (policies == null) {
                policies = this.getDefaultPolicy(direction);
            }
        }
        return StreamUtil_1.default.fromIterator(this.iterator(direction, policies));
    }
    iterator(direction = Direction_1.default.DEFAULT, policies) {
        if (direction instanceof ImmutablePrioritySet_1.default) {
            if (arguments.length !== 1) {
                throw EtudeError_1.default.forIllegalArgument(Scale, arguments, "Invalid number of arguments");
            }
            policies = direction;
            direction = Direction_1.default.DEFAULT;
        }
        else if (direction instanceof Direction_1.default) {
            if (policies == null) {
                policies = this.getDefaultPolicy(direction);
            }
        }
        const it = CircularIterator_1.default.of(this.quality.getStepPattern(direction));
        return InfiniteIterator_1.default.of(Pitch_1.default.fromProgramNumber(this.pitch.getProgramNumber(), policies).get(), previous => previous.step(it.next().value, policies).get());
    }
    getPitches(direction = Direction_1.default.DEFAULT, policies) {
        if (direction instanceof ImmutablePrioritySet_1.default) {
            if (arguments.length !== 1) {
                throw EtudeError_1.default.forIllegalArgument(Scale, arguments, "Invalid number of arguments");
            }
            policies = direction;
            direction = Direction_1.default.DEFAULT;
        }
        else if (direction instanceof Direction_1.default) {
            if (policies == null) {
                policies = this.getDefaultPolicy(direction);
            }
        }
        return this.stream(direction, policies).limit(this.quality.getStepPattern(direction).length);
    }
    toString(direction = Direction_1.default.DEFAULT) {
        return "[" + this.getPitches(direction).join(", ") + "]";
    }
    getPitch() {
        return this.pitch;
    }
    getQuality() {
        return this.quality;
    }
}
(function (Scale) {
    class Quality {
        // descending: reverse ascending and negate all values (e.g. [1, 3, 5] -> [-5, -3, -1])
        constructor(ascending = [], descending = ascending.slice().reverse().map(n => -n)) {
            this.ascending = ascending;
            this.descending = descending;
            if (ascending.length === 0) {
                throw EtudeError_1.default.forIllegalArgument(Quality, ascending, "step pattern array should not be empty");
            }
        }
        getStepPattern(direction = Direction_1.default.DEFAULT) {
            switch (direction) {
                case Direction_1.default.ASCENDING: return this.ascending.slice();
                case Direction_1.default.DESCENDING: return this.descending.slice();
                default: throw new AssertionError_1.default("Invalid direction: " + direction);
            }
        }
        isOctaveRepeating(direction = Direction_1.default.DEFAULT) {
            return Math.abs(this.getStepPattern(direction).reduce(MathUtil_1.default.add, 0)) === MusicConstants_1.default.KEYS_IN_OCTAVE;
        }
    }
    Quality.MAJOR = new Quality([2, 2, 1, 2, 2, 2, 1]);
    Quality.NATURAL_MINOR = new Quality([2, 1, 2, 2, 1, 2, 2]);
    Quality.HARMONIC_MINOR = new Quality([2, 1, 2, 2, 1, 3, 1]);
    Quality.MELODIC_MINOR = new Quality([2, 1, 2, 2, 2, 2, 1], [-2, -2, -1, -2, -2, -1, -2]);
    Quality.CHROMATIC = new Quality([1]);
    Quality.WHOLE_TONE = new Quality([2]);
    Scale.Quality = Quality;
})(Scale || (Scale = {}));
exports.default = Scale;
